<template>
  <!-- Member Photo Update Modal -->
  <div class="modal fade" id="memberPhotoModal" tabindex="-1" aria-labelledby="memberPhotoModalLabel" aria-hidden="true" ref="photoModal">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header text-white">
          <h5 class="modal-title" id="memberPhotoModalLabel">
            <i class="bx bx-camera me-2"></i>
            Update Member Photo
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body p-4">
          <!-- Alert Messages -->
          <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
            {{ alertMessage }}
            <button type="button" class="btn-close" @click="clearAlert"></button>
          </div>
          <div v-if="alert.error" class="alert alert-danger alert-dismissible fade show" role="alert">
            {{ alertMessage }}
            <button type="button" class="btn-close" @click="clearAlert"></button>
          </div>
          <div v-if="alert.info" class="alert alert-info alert-dismissible fade show" role="alert">
            {{ alertMessage }}
            <button type="button" class="btn-close" @click="clearAlert"></button>
          </div>

          <!-- Member Information Header -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="d-flex align-items-center">
                <div class="avatar-lg bg-soft-primary rounded-circle me-3 d-flex align-items-center justify-content-center">
                  <img v-if="memberInfo.profile_photo"
                       :src="getPhotoUrl(memberInfo.profile_photo)"
                       class="rounded-circle"
                       style="width: 60px; height: 60px; object-fit: cover;"
                       alt="Current Photo">
                  <i v-else class="bx bx-user display-6 text-primary"></i>
                </div>
                <div>
                  <h6 class="mb-1">{{ memberInfo.fullName }}</h6>
                  <p class="text-muted mb-0">CBHI ID: {{ memberInfo.cbhiNumber }}</p>
                  <small v-if="memberInfo.photo_upload_date" class="text-info">
                    Photo uploaded: {{ formatDate(memberInfo.photo_upload_date) }}
                  </small>
                  <small v-else class="text-warning">No photo on file</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Photo Requirements Notice -->
          <div class="alert alert-info border-info mb-4">
            <i class="bx bx-info-circle me-2"></i>
            <strong>Photo Requirements:</strong>
            <ul class="mb-0 mt-2">
              <li>Photo must be taken within the last month</li>
              <li>Clear, front-facing portrait photo</li>
              <li>Good lighting and visibility of face</li>
              <li>Maximum file size: 5MB</li>
              <li>Supported formats: JPG, PNG, JPEG</li>
            </ul>
          </div>

          <!-- Photo Capture/Upload Options -->
          <div class="row">
            <div class="col-12">
              <!-- Tab Navigation -->
              <ul class="nav nav-pills justify-content-center mb-4" role="tablist">
                <li class="nav-item" role="presentation">
                  <button class="nav-link active"
                          id="webcam-tab"
                          data-bs-toggle="pill"
                          data-bs-target="#webcam-pane"
                          type="button"
                          role="tab">
                    <i class="bx bx-camera me-2"></i>
                    Take Photo
                  </button>
                </li>
                <li class="nav-item" role="presentation">
                  <button class="nav-link"
                          id="upload-tab"
                          data-bs-toggle="pill"
                          data-bs-target="#upload-pane"
                          type="button"
                          role="tab">
                    <i class="bx bx-upload me-2"></i>
                    Upload File
                  </button>
                </li>
              </ul>

              <!-- Tab Content -->
              <div class="tab-content">
                <!-- Webcam Capture Tab -->
                <div class="tab-pane fade show active" id="webcam-pane" role="tabpanel">
                  <div class="text-center">
                    <!-- Video Preview -->
                    <div class="position-relative d-inline-block mb-3">
                      <video ref="video"
                             width="400"
                             height="300"
                             autoplay
                             muted
                             v-show="webcamActive"
                             class="border rounded shadow-sm">
                      </video>

                      <!-- Canvas for capturing photo -->
                      <canvas ref="canvas"
                              width="400"
                              height="300"
                              v-show="capturedImage"
                              class="border rounded shadow-sm">
                      </canvas>

                      <!-- Placeholder when webcam is not active -->
                      <div v-show="!webcamActive && !capturedImage"
                           class="border rounded shadow-sm d-flex align-items-center justify-content-center bg-light"
                           style="width: 400px; height: 300px;">
                        <div class="text-center">
                          <i class="bx bx-camera display-1 text-muted"></i>
                          <p class="text-muted mt-2">Camera not active</p>
                        </div>
                      </div>
                    </div>

                    <!-- Camera Controls -->
                    <div class="d-flex justify-content-center gap-2 mb-3">
                      <button v-if="!webcamActive"
                              @click="startWebcam"
                              class="btn btn-primary"
                              :disabled="processingWebcam">
                        <i class="bx bx-video me-2"></i>
                        {{ processingWebcam ? 'Starting Camera...' : 'Start Camera' }}
                      </button>

                      <button v-if="webcamActive && !capturedImage"
                              @click="capturePhoto"
                              class="btn btn-success">
                        <i class="bx bx-camera me-2"></i>
                        Capture Photo
                      </button>

                      <button v-if="webcamActive"
                              @click="stopWebcam"
                              class="btn btn-outline-secondary">
                        <i class="bx bx-stop me-2"></i>
                        Stop Camera
                      </button>
                    </div>

                    <!-- Captured Photo Controls -->
                    <div v-if="capturedImage" class="d-flex justify-content-center gap-2">
                      <button @click="retakePhoto"
                              class="btn btn-outline-warning">
                        <i class="bx bx-refresh me-2"></i>
                        Retake
                      </button>
                      <button @click="useCapturedPhoto"
                              class="btn btn-success">
                        <i class="bx bx-check me-2"></i>
                        Use This Photo
                      </button>
                    </div>
                  </div>
                </div>

                <!-- File Upload Tab -->
                <div class="tab-pane fade" id="upload-pane" role="tabpanel">
                  <div class="text-center">
                    <!-- File Drop Zone -->
                    <div class="border-dashed border-2 border-primary rounded p-5 mb-3"
                         style="border-style: dashed !important;"
                         @dragover.prevent
                         @drop.prevent="handleFileDrop"
                         @click="$refs.fileInput.click()">
                      <i class="bx bx-cloud-upload display-3 text-primary mb-3"></i>
                      <h5 class="text-primary">Drop photo here or click to browse</h5>
                      <p class="text-muted mb-0">
                        Select a recent photo (JPG, PNG, JPEG) up to 5MB
                      </p>

                      <!-- Hidden File Input -->
                      <input ref="fileInput"
                             type="file"
                             accept="image/jpeg,image/jpg,image/png"
                             @change="handleFileSelect"
                             style="display: none;">
                    </div>

                    <!-- Selected File Preview -->
                    <div v-if="selectedFile" class="card">
                      <div class="card-body">
                        <div class="row align-items-center">
                          <div class="col-md-4">
                            <img :src="filePreview"
                                 class="img-fluid rounded"
                                 style="max-height: 200px; object-fit: cover;"
                                 alt="Photo Preview">
                          </div>
                          <div class="col-md-8">
                            <h6 class="mb-2">{{ selectedFile.name }}</h6>
                            <p class="text-muted mb-2">
                              <small>
                                Size: {{ formatFileSize(selectedFile.size) }}<br>
                                Type: {{ selectedFile.type }}<br>
                                Last Modified: {{ formatDate(new Date(selectedFile.lastModified)) }}
                              </small>
                            </p>
                            <div class="d-flex gap-2">
                              <button @click="clearSelectedFile"
                                      class="btn btn-outline-danger btn-sm">
                                <i class="bx bx-trash me-1"></i>
                                Remove
                              </button>
                              <button @click="useSelectedFile"
                                      class="btn btn-success btn-sm">
                                <i class="bx bx-check me-1"></i>
                                Use This Photo
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer bg-light">
          <button type="button"
                  class="btn btn-secondary"
                  data-bs-dismiss="modal">
            <i class="bx bx-x me-2"></i>
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "MemberPhotoModal",

  props: {
    memberInfo: {
      type: Object,
      required: true
    }
  },

  emits: ['photo-updated', 'photo-error'],

  data() {
    return {
      // Alert system
      alert: {
        success: false,
        error: false,
        info: false
      },
      alertMessage: '',

      // Webcam related
      webcamActive: false,
      processingWebcam: false,
      mediaStream: null,
      capturedImage: false,

      // File upload related
      selectedFile: null,
      filePreview: null,

      // Upload processing
      uploading: false,
      uploadProgress: 0
    };
  },

  mounted() {
    // Initialize modal event listeners
    this.$refs.photoModal.addEventListener('hidden.bs.modal', () => {
      this.cleanup();
    });
  },

  beforeUnmount() {
    this.cleanup();
  },

  methods: {
    // Modal control methods
    openModal() {
      const modal = new bootstrap.Modal(this.$refs.photoModal);
      modal.show();
      this.clearAlert();
    },

    closeModal() {
      const modal = bootstrap.Modal.getInstance(this.$refs.photoModal);
      if (modal) {
        modal.hide();
      }
    },

    // Alert methods
    showAlert(type, message) {
      this.clearAlert();
      this.alert[type] = true;
      this.alertMessage = message;

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlert();
        }, 5000);
      }
    },

    clearAlert() {
      this.alert = {
        success: false,
        error: false,
        info: false
      };
      this.alertMessage = '';
    },

    // Webcam methods
    async startWebcam() {
      this.processingWebcam = true;
      this.clearAlert();

      try {
        const constraints = {
          video: {
            width: { ideal: 400 },
            height: { ideal: 300 },
            facingMode: 'user'
          },
          audio: false
        };

        this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        this.$refs.video.srcObject = this.mediaStream;
        this.webcamActive = true;
        this.capturedImage = false;

        this.showAlert('info', 'Camera started successfully. Position yourself and click "Capture Photo".');
      } catch (error) {
        console.error('Error accessing webcam:', error);
        this.showAlert('error', 'Unable to access camera. Please check permissions or try uploading a file instead.');
      } finally {
        this.processingWebcam = false;
      }
    },

    stopWebcam() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
        this.mediaStream = null;
      }
      this.webcamActive = false;
      this.capturedImage = false;
    },

    capturePhoto() {
      if (!this.webcamActive) return;

      const canvas = this.$refs.canvas;
      const video = this.$refs.video;
      const ctx = canvas.getContext('2d');

      // Draw video frame to canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      this.capturedImage = true;
      this.showAlert('success', 'Photo captured! Review the image and click "Use This Photo" if you\'re satisfied.');
    },

    retakePhoto() {
      this.capturedImage = false;
      this.clearAlert();
    },

    async useCapturedPhoto() {
      const canvas = this.$refs.canvas;

      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], `member-photo-${Date.now()}.jpg`, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });

          await this.uploadPhoto(file);
        }
      }, 'image/jpeg', 0.8);
    },

    // File upload methods
    handleFileDrop(event) {
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.processSelectedFile(files[0]);
      }
    },

    handleFileSelect(event) {
      const files = event.target.files;
      if (files.length > 0) {
        this.processSelectedFile(files[0]);
      }
    },

    processSelectedFile(file) {
      // Validate file
      if (!this.validateFile(file)) return;

      this.selectedFile = file;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.filePreview = e.target.result;
      };
      reader.readAsDataURL(file);

      this.showAlert('success', 'Photo selected successfully. Review and click "Use This Photo" to upload.');
    },

    validateFile(file) {
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        this.showAlert('error', 'Please select a valid image file (JPG, PNG, JPEG).');
        return false;
      }

      // Check file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        this.showAlert('error', 'File size must be less than 5MB.');
        return false;
      }

      return true;
    },

    clearSelectedFile() {
      this.selectedFile = null;
      this.filePreview = null;
      this.$refs.fileInput.value = '';
      this.clearAlert();
    },

    async useSelectedFile() {
      if (!this.selectedFile) return;
      await this.uploadPhoto(this.selectedFile);
    },

    // Upload method
    async uploadPhoto(file) {
      this.uploading = true;
      this.uploadProgress = 0;

      try {
        // Create FormData
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('function', 'updateMemberPhoto');
        formData.append('cbhiNumber', this.memberInfo.cbhiNumber);

        // Upload with progress tracking
        const response = await fetch(authUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: formData
        });

        const result = await response.json();

        if (result.success) {
          this.showAlert('success', 'Photo updated successfully!');

          // Emit success event
          this.$emit('photo-updated', {
            success: true,
            message: 'Member photo updated successfully',
            photoPath: result.data.photoPath
          });

          // Close modal after delay
          setTimeout(() => {
            this.closeModal();
          }, 2000);

        } else {
          throw new Error(result.message || 'Upload failed');
        }

      } catch (error) {
        console.error('Photo upload error:', error);
        this.showAlert('error', error.message || 'Failed to upload photo. Please try again.');

        this.$emit('photo-error', {
          success: false,
          message: error.message || 'Failed to upload photo'
        });
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
      }
    },

    // Cleanup method
    cleanup() {
      this.stopWebcam();
      this.clearSelectedFile();
      this.clearAlert();
      this.uploading = false;
      this.uploadProgress = 0;
    },

    // ✅ FIXED: Utility method to get photo URL (same as MemberDetails.vue)
    getPhotoUrl(photoPath) {
      if (!photoPath) return '';

      // If it's already a complete URL, return as-is
      if (photoPath.startsWith('http')) {
        return photoPath;
      }

      // Construct full API URL for member photos
      return `https://rcmsuat.craftsilicon.com/api1/uploads/member-photos/${photoPath}`;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
/* Modal specific styles */
.border-dashed {
  border-style: dashed !important;
  cursor: pointer;
  transition: all 0.3s ease;
}

.border-dashed:hover {
  background-color: rgba(13, 110, 253, 0.1);
  border-color: #0d6efd !important;
}

/* Video and canvas styling */
video, canvas {
  max-width: 100%;
  height: auto;
}

/* Avatar styling */
.avatar-lg {
  width: 80px;
  height: 80px;
}

/* Tab styling */
.nav-pills .nav-link {
  border-radius: 50px;
  padding: 10px 20px;
  margin: 0 5px;
}

.nav-pills .nav-link.active {
  background-color: #0d6efd;
  color: white;
}

/* Card styling */
.card {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
}

/* Button styling */
.btn {
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

/* Alert styling */
.alert {
  border-radius: 0.5rem;
  border: 1px solid transparent;
}

/* Progress styling for upload */
.upload-progress {
  height: 4px;
  background-color: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
}

.upload-progress-bar {
  height: 100%;
  background: linear-gradient(45deg, #007bff, #0056b3);
  transition: width 0.3s ease;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modal-lg {
    max-width: 95%;
    margin: 1rem;
  }

  video, canvas {
    width: 100%;
    max-width: 300px;
  }

  .avatar-lg {
    width: 60px;
    height: 60px;
  }
}
</style>